/**
 * The ONLY module that reads configuration (07 §4.2).
 *
 * Precedence: runtime `window.__HADIR_CONFIG__` (rendered by the container
 * entrypoint) → build-time `import.meta.env` (the dev path) → a dev-safe default.
 * An empty runtime string means "unset", never "empty value".
 */

export interface RuntimeConfig {
  VITE_API_URL?: string;
  VITE_SOCKET_URL?: string;
  VITE_HEALTH_URL?: string;
  VITE_STUDENT_APP_URL?: string;
  VITE_MAP_TILE_URL?: string;
  VITE_DEFAULT_CENTER?: string;
  VITE_SENTRY_DSN?: string;
}

const runtime: RuntimeConfig =
  (globalThis as typeof globalThis & { __HADIR_CONFIG__?: RuntimeConfig }).__HADIR_CONFIG__ ?? {};

/** First non-empty value wins; `undefined` and `''` are both "unset". */
function pick(...values: Array<string | undefined>): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return '';
}

/** Strip a trailing slash so callers can always join with a leading-slash path. */
function stripTrailingSlash(value: string): string {
  return value.endsWith('/') ? value.slice(0, -1) : value;
}

/**
 * The health endpoints sit outside the `/api/v1` prefix (04 §4.11), so the base
 * for `/health/ready` is the API base with the version prefix removed:
 *   `/api/v1`                       -> `/health`
 *   `https://api.example.com/api/v1`-> `https://api.example.com/health`
 */
function deriveHealthBase(apiUrl: string): string {
  return `${stripTrailingSlash(apiUrl.replace(/\/api\/v\d+\/?$/, ''))}/health`;
}

const apiUrl = stripTrailingSlash(
  pick(runtime.VITE_API_URL, import.meta.env.VITE_API_URL, '/api/v1'),
);

const socketUrl = stripTrailingSlash(
  pick(runtime.VITE_SOCKET_URL, import.meta.env.VITE_SOCKET_URL),
);

/** Parse `"lat,lng"`; falls back to central Cairo (05 §2.6). */
function parseCenter(value: string): { lat: number; lng: number } {
  const parts = value.split(',');
  const lat = Number(parts[0]);
  const lng = Number(parts[1]);
  if (parts.length === 2 && Number.isFinite(lat) && Number.isFinite(lng)) return { lat, lng };
  return { lat: 30.0444, lng: 31.2357 };
}

export const config = {
  /** REST base, e.g. `/api/v1` in dev. Never ends with a slash. */
  apiUrl,
  /** Socket.IO base; `''` means same origin. The `/ta` namespace is appended in code. */
  socketUrl,
  /** Base for `/health/live`, `/health/ready`, `/health/version`. Never ends with a slash. */
  healthUrl: stripTrailingSlash(
    pick(runtime.VITE_HEALTH_URL, import.meta.env.VITE_HEALTH_URL, deriveHealthBase(apiUrl)),
  ),
  /** Shown under the projected QR as a manual fallback (05 §2.8). */
  studentAppUrl: stripTrailingSlash(
    pick(
      runtime.VITE_STUDENT_APP_URL,
      import.meta.env.VITE_STUDENT_APP_URL,
      'https://app.hadir.local',
    ),
  ),
  /** Leaflet tile template for the section location picker (05 §2.6). */
  mapTileUrl: pick(
    runtime.VITE_MAP_TILE_URL,
    import.meta.env.VITE_MAP_TILE_URL,
    'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
  ),
  defaultCenter: parseCenter(
    pick(runtime.VITE_DEFAULT_CENTER, import.meta.env.VITE_DEFAULT_CENTER, '30.0444,31.2357'),
  ),
  sentryDsn: pick(runtime.VITE_SENTRY_DSN, import.meta.env.VITE_SENTRY_DSN),
  /** Build-time only (the git SHA), shown in the footer. */
  appVersion: pick(import.meta.env.VITE_APP_VERSION, 'dev'),
} as const;

export type AppConfig = typeof config;
